/**
 * Central role definitions — importable from any part of the app.
 *
 * Keep this file *tiny* — the whole point is that adding a new role
 * (partner_auditor, customer_admin, supplier_lite, …) is a single-line
 * change here + one line in navigation.js + one entry in workspace.js.
 */
export const ROLES = Object.freeze({
  ESG: 'esg',
  AUDITOR: 'auditor',
  CFO: 'cfo',
  CPO: 'cpo',
  CSO: 'cso',
  SUPPLIER: 'supplier',
  ADMIN: 'admin',
  USER: 'user',
});

/** Rich per-role metadata: label + post-login landing + capability list. */
export const ROLE_META = {
  [ROLES.ESG]: {
    label: 'Sustainability Manager',
    landing: '/workspace',
    permissions: ['view_pcf', 'view_reports', 'view_compliance', 'manage_products'],
  },
  [ROLES.AUDITOR]: {
    label: 'Auditor',
    landing: '/workspace',
    permissions: ['view_evidence', 'view_trust', 'approve_audit'],
  },
  [ROLES.CFO]: {
    label: 'Chief Financial Officer',
    landing: '/workspace',
    permissions: ['view_cost', 'view_cbam', 'view_roi'],
  },
  [ROLES.CPO]: {
    label: 'Procurement Officer',
    landing: '/workspace',
    permissions: ['view_suppliers', 'manage_suppliers'],
  },
  [ROLES.CSO]: {
    label: 'Chief Sustainability Officer',
    landing: '/workspace',
    permissions: ['view_pcf', 'view_reports', 'strategy'],
  },
  [ROLES.SUPPLIER]: {
    label: 'Supplier',
    landing: '/workspace',
    permissions: ['upload_data', 'view_own_declarations'],
  },
  [ROLES.ADMIN]: {
    label: 'Administrator',
    landing: '/workspace',
    permissions: ['manage_users', 'manage_company', 'billing', '*'],
  },
  [ROLES.USER]: {
    label: 'Team Member',
    landing: '/workspace',
    permissions: ['upload_data', 'create_pcf'],
  },
};

/** Back-compat alias: legacy imports do `import { ROLE_LABELS }`. */
export const ROLE_LABELS = Object.fromEntries(
  Object.entries(ROLE_META).map(([k, v]) => [k, v.label])
);

/** Resolve the effective role from a user object, falling back safely. */
export function resolveRole(user) {
  if (!user) return ROLES.USER;
  const raw = (user.role || '').toLowerCase();
  return Object.values(ROLES).includes(raw) ? raw : ROLES.USER;
}

export function getRoleMeta(role) {
  return ROLE_META[role] || ROLE_META[ROLES.USER];
}

export function hasPermission(user, perm) {
  const meta = ROLE_META[resolveRole(user)];
  return !!meta && (meta.permissions.includes('*') || meta.permissions.includes(perm));
}
